import { Component, OnInit, OnDestroy, ChangeDetectorRef, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { PLATFORM_ID } from '@angular/core';
import { environment } from './../../environment/environment';

interface Producto {
  _id?: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  modoEdicion?: boolean;
}

interface ProductoEdicion extends Producto {
  originalData?: Producto;
}

type Modo = 'crear' | 'buscar' | 'mostrar' | null;

@Component({
  selector: 'app-dasboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dasboard.html',
  styleUrls: ['./dasboard.scss'], // 👈 corregido
})
export class Dasboard implements OnInit, OnDestroy {
  modo: Modo = null;
  producto: Producto = { name: '', description: '', price: 0, category: '', stock: 0 };
  productos: ProductoEdicion[] = [];
  busqueda: string = '';
  mensaje: string = '';
  timeoutId: any;
  cargando: boolean = false;

  mostrarConfirmacion: boolean = false;
  productoAEliminar: Producto | null = null;
  mensajeConfirmacion: string = '';

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private titleService: Title,
    private metaService: Meta
  ) {}

  ngOnInit(): void {
    // SEO dinámico para Dashboard
    this.titleService.setTitle('Pegaespumas | Dashboard');
    this.metaService.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    this.metaService.updateTag({
      name: 'description',
      content:
        'Panel de control interno de Pegaespumas. Acceso restringido a usuarios autorizados.',
    });
  }

  ngOnDestroy() {
    this.detenerInactivityTimer();
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      document.cookie = 'token=; Max-Age=0; path=/';
      location.href = '/';
    }
  }

  setModo(m: 'crear' | 'buscar' | 'mostrar') {
    this.detenerInactivityTimer();
    this.modo = m;
    this.mensaje = '';
    this.cargando = false;
    this.cdr.detectChanges();

    if (m === 'crear') this.resetForm();
    if (m === 'mostrar') this.cargarProductos();

    this.startInactivityTimer();
  }

  detenerInactivityTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  startInactivityTimer() {
    this.detenerInactivityTimer();
    this.timeoutId = setTimeout(() => {
      if (this.modo) {
        this.mensaje = `Se cerró el modo "${this.modo}" por inactividad`;
        this.modo = null;
        this.cargando = false;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.mensaje = '';
          this.cdr.detectChanges();
        }, 3000);
      }
    }, 30000);
  }

  guardarProducto() {
    if (!this.producto.name || !this.producto.description || !this.producto.category) {
      this.mensaje = 'Por favor complete todos los campos obligatorios';
      return;
    }

    this.startInactivityTimer();
    this.cargando = true;

    this.http
      .post<Producto>(`${environment.apiUrl}/api/products`, this.producto, {
        withCredentials: true,
      })
      .subscribe({
        next: () => {
          this.mensaje = '✅ Producto guardado correctamente';
          this.resetForm();
          this.cargando = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.mensaje = '';
            this.cdr.detectChanges();
          }, 3000);
        },
        error: (err) => {
          console.error('Error en guardarProducto:', err);
          this.mensaje = '❌ Error al guardar producto';
          this.cargando = false;
          this.cdr.detectChanges();
        },
      });
  }

  resetForm() {
    this.producto = { name: '', description: '', price: 0, category: '', stock: 0 };
  }

  buscar() {
    this.startInactivityTimer();
    if (this.busqueda.trim().length < 3) {
      this.mensaje = 'Escriba al menos 3 letras para buscar';
      return;
    }

    this.cargando = true;
    this.http
      .get<Producto[]>(
        `${environment.apiUrl}/api/products/search?q=${encodeURIComponent(this.busqueda)}`,
        {
          withCredentials: true,
        }
      )
      .subscribe({
        next: (res) => {
          this.productos = res.map((p) => ({
            ...p,
            modoEdicion: false,
            originalData: { ...p },
          }));
          this.mensaje =
            res.length > 0
              ? `✅ ${res.length} resultados encontrados`
              : '❌ No se encontraron productos';
          this.cargando = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error en búsqueda:', err);
          this.mensaje = '❌ Error en la búsqueda';
          this.cargando = false;
          this.cdr.detectChanges();
        },
      });
  }

  cargarProductos() {
    this.cargando = true;
    this.http
      .get<Producto[]>(`${environment.apiUrl}/api/products`, { withCredentials: true })
      .subscribe({
        next: (res) => {
          this.productos = res.map((p) => ({
            ...p,
            modoEdicion: false,
            originalData: { ...p },
          }));
          this.cargando = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error al cargar productos:', err);
          this.mensaje = '❌ Error al cargar productos';
          this.cargando = false;
          this.cdr.detectChanges();
        },
      });
  }

  activarEdicion(p: ProductoEdicion) {
    this.startInactivityTimer();
    this.productos.forEach((producto) => {
      if (producto !== p) producto.modoEdicion = false;
    });
    p.originalData = { ...p };
    p.modoEdicion = true;
    this.cdr.detectChanges();
  }

  cancelarEdicion(p: ProductoEdicion) {
    if (p.originalData) Object.assign(p, p.originalData);
    p.modoEdicion = false;
    this.cdr.detectChanges();
  }

  guardarEdicion(p: ProductoEdicion) {
    if (!p._id) return;
    this.startInactivityTimer();
    this.cargando = true;

    const productoParaGuardar: Producto = {
      _id: p._id,
      name: p.name,
      description: p.description,
      price: p.price,
      category: p.category,
      stock: p.stock,
    };

    this.http
      .put(`${environment.apiUrl}/api/products/${p._id}`, productoParaGuardar, {
        withCredentials: true,
      })
      .subscribe({
        next: () => {
          this.mensaje = `✅ Producto actualizado correctamente`;
          p.modoEdicion = false;
          p.originalData = undefined;
          this.cargando = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.mensaje = '';
            this.cdr.detectChanges();
          }, 3000);
        },
        error: (err) => {
          console.error('Error al actualizar:', err);
          this.mensaje = '❌ Error al actualizar producto';
          if (p.originalData) Object.assign(p, p.originalData);
          p.modoEdicion = false;
          this.cargando = false;
          this.cdr.detectChanges();
        },
      });
  }

  solicitarEliminacion(p: Producto) {
    if (!p._id) return;
    this.productoAEliminar = p;
    this.mensajeConfirmacion = `¿


Estás seguro de que deseas eliminar "${p.name}"?`;
    this.mostrarConfirmacion = true;
    this.cdr.detectChanges();
  }

  confirmarEliminacion() {
    if (!this.productoAEliminar || !this.productoAEliminar._id) {
      this.cancelarEliminacion();
      return;
    }

    const productoId = this.productoAEliminar._id;
    const productoNombre = this.productoAEliminar.name;

    this.cargando = true;
    this.mostrarConfirmacion = false;

    // ✅ CORRECTO: DELETE a /api/products/:id
    this.http
      .delete(`${environment.apiUrl}/api/products/${productoId}`, { withCredentials: true })
      .subscribe({
        next: () => {
          this.mensaje = `✅ Producto "${productoNombre}" eliminado correctamente`;
          this.productos = this.productos.filter((p) => p._id !== productoId);
          this.cargando = false;
          this.productoAEliminar = null;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.mensaje = '';
            this.cdr.detectChanges();
          }, 3000);
        },
        error: (err) => {
          console.error('Error al eliminar:', err);
          this.mensaje = '❌ Error al eliminar el producto';
          this.cargando = false;
          this.productoAEliminar = null;
          this.cdr.detectChanges();
        },
      });
  }

  cancelarEliminacion() {
    this.mostrarConfirmacion = false;
    this.productoAEliminar = null;
    this.mensajeConfirmacion = '';
    this.cdr.detectChanges();
  }
}
